import { CheckCircle2 } from 'lucide-react'
import { useUserRole } from '@/hooks/useUserRole'
import type { ChargebackHistoryTabProps } from '../../types/components'
import type { PaymentAdjustmentHistoryRow } from '../../types/api'
import {
  CHARGEBACK_DETAIL_CARD_CLASS,
  CHARGEBACK_DETAIL_TABLE_HEAD_CLASS,
  CHARGEBACK_DETAIL_TABLE_TD_CLASS,
  CHARGEBACK_DETAIL_TABLE_TH_CLASS,
} from '../../constants/chargebackDetailConstants'
import { display, formatDateTime } from '../../utils/chargebackDetailFormatters'
import { ChargebackAddNotes } from './ChargebackAddNotes'

export function ChargebackHistoryTab({ page }: ChargebackHistoryTabProps) {
  const { isLibertyRole } = useUserRole()
  const { detail, notes, noteDraft, setNoteDraft, saving, addNote } = page

  const historyRows = detail?.payment_adjustment_history

  return (
    <div className="space-y-6">
      {/* 1. History Table */}
      {isLibertyRole && Array.isArray(historyRows) && historyRows.length ? (
        <div className={`${CHARGEBACK_DETAIL_CARD_CLASS} overflow-hidden p-0`}>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className={CHARGEBACK_DETAIL_TABLE_HEAD_CLASS}>
                <tr>
                  <th className={CHARGEBACK_DETAIL_TABLE_TH_CLASS}>Status</th>
                  <th className={CHARGEBACK_DETAIL_TABLE_TH_CLASS}>Progress</th>
                  <th className={CHARGEBACK_DETAIL_TABLE_TH_CLASS}>Cancel Application</th>
                  <th className={CHARGEBACK_DETAIL_TABLE_TH_CLASS}>Updated By</th>
                  <th className={CHARGEBACK_DETAIL_TABLE_TH_CLASS}>Updated At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {historyRows.map((row: PaymentAdjustmentHistoryRow, index: number) => {
                  const statusVal = row.chargeback_status
                  const isCompleted =
                    statusVal &&
                    ['CHARGEBACK_COMPLETE', 'WON_CREDIT', 'LOST'].includes(String(statusVal).toUpperCase())
                  const isCancel =
                    ['CHARGEBACK_CANCEL_CERTIFICATE', 'CANCEL_CERTIFICATE'].includes(
                      String(row.adjustment_subtype ?? ''),
                    ) || String(row.cancelled_application ?? '') === '1'

                  return (
                    <tr key={`${row.executive_date ?? ''}-${index}`}>
                      <td className={CHARGEBACK_DETAIL_TABLE_TD_CLASS}>
                        <div className="flex items-center gap-2">
                          {isCompleted ? (
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden />
                          ) : (
                            <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-slate-300" aria-hidden />
                          )}
                          <span className="font-bold text-slate-900">{display(statusVal)}</span>
                        </div>
                      </td>
                      <td className={`${CHARGEBACK_DETAIL_TABLE_TD_CLASS} font-medium text-slate-600`}>
                        {display(row.chargeback_type)}
                      </td>
                      <td className={`${CHARGEBACK_DETAIL_TABLE_TD_CLASS} font-medium text-slate-700`}>
                        {isCancel ? 'Yes' : 'No'}
                      </td>
                      <td className={`${CHARGEBACK_DETAIL_TABLE_TD_CLASS} font-semibold text-slate-800`}>
                        {display(row.username)}
                      </td>
                      <td className={`${CHARGEBACK_DETAIL_TABLE_TD_CLASS} font-medium text-slate-600`}>
                        {formatDateTime(row.executive_date)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className={`${CHARGEBACK_DETAIL_CARD_CLASS} px-6 py-5 text-sm text-slate-500`}>
          No Records
        </div>
      )}

      {/* 2. Add Notes Section */}
      <ChargebackAddNotes
        noteDraft={noteDraft}
        setNoteDraft={setNoteDraft}
        saving={saving}
        addNote={addNote}
        helperText="Your note will be added to the chargeback history."
      />

      {/* 3. Notes List */}
      {notes.length ? (
        <div className="space-y-3">
          {notes.map((note, index) => (
            <div
              key={`${note.created_at ?? ''}-${index}`}
              className={`${CHARGEBACK_DETAIL_CARD_CLASS} flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:gap-8`}
            >
              <div className="min-w-[12rem] space-y-0.5 sm:border-r sm:border-slate-100 sm:pr-8">
                <p className="text-sm font-bold text-slate-900">{display(note.name)}</p>
                <p className="text-xs text-slate-500">{formatDateTime(note.created_at)}</p>
              </div>
              <p className="text-sm font-medium text-slate-800">{display(note.notes)}</p>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
